/**
 * Geometry and phase driver for the Ten Four lemniscate loader.
 *
 * The spiral is the official Ten Four mark: eight golden-ratio quarter-circle
 * arcs (radii 840 -> 28.93, each ~= previous / phi) with the official
 * transform chain flattened in, sampled at equal arc length. The lemniscate is
 * a Bernoulli lemniscate whose seam (start/end station) sits at the self
 * crossing, so the spiral's two open endpoints converge to a single point when
 * the curve closes. Index k on one curve corresponds to index k on the other;
 * morphing is a point-wise lerp between the two station arrays.
 *
 * The solid-orange snake is a dash window [w0, w0 + len] over a pathLength-normalized
 * stroke. Every phase is value- and velocity-continuous at its boundaries by
 * construction (mod-1 arithmetic), which is what makes the loop and both
 * handoffs seamless.
 */

export type Point = [number, number]

export const VIEWBOX = 120
export const STROKE = 9
export const SEGMENTS = 240
export const SNAKE = 0.38 // snake length as a fraction of total path length
export const PERIOD_MS = 1400 // one loop lap
export const MORPH_MS = 600 // spiral <-> lemniscate morph
export const SPIRAL_HEIGHT = 78 // spiral centerline height in viewBox units
export const LEMNISCATE_A = 50 // Bernoulli half-width in viewBox units

/** Ratified solid Ten Four orange used by the static logo and loading mark. */
export const MARK_COLOR = '#EC4724'

/** The official mark's eight arcs: radii and chained endpoints (local space). */
const RADII = [840, 519.1486, 320.8514, 198.2971, 122.5543, 75.7428, 46.8116, 28.9312]
const ARC_ENDPOINTS: ReadonlyArray<Point> = [
  [0, 840],
  [840, 0],
  [1359.1486, 519.1486],
  [1038.2971, 840],
  [840, 641.7029],
  [962.5543, 519.1486],
  [1038.2971, 594.8913],
  [991.4855, 641.7029],
  [962.5543, 612.7717],
]

interface Arc {
  r: number
  cx: number
  cy: number
  th1: number
  dth: number
  len: number
}

/** SVG F.6.5 endpoint -> center conversion for the mark's circular arcs (largeArc=0, sweep=1). */
function spiralArcs(): Arc[] {
  return RADII.map((r, i) => {
    const [x1, y1] = ARC_ENDPOINTS[i]
    const [x2, y2] = ARC_ENDPOINTS[i + 1]
    const mx = (x1 - x2) / 2
    const my = (y1 - y2) / 2
    const d2 = mx * mx + my * my
    const f = Math.sqrt(Math.max(0, (r * r - d2) / d2))
    const cx = f * my + (x1 + x2) / 2
    const cy = -f * mx + (y1 + y2) / 2
    const th1 = Math.atan2(y1 - cy, x1 - cx)
    let dth = Math.atan2(y2 - cy, x2 - cx) - th1
    while (dth <= 0) dth += 2 * Math.PI
    return { r, cx, cy, th1, dth, len: r * dth }
  })
}

/** Equal-arc-length spiral stations, fitted upright and centered in the viewBox. */
function sampleSpiral(n: number, heightUnits: number): Point[] {
  const arcs = spiralArcs()
  const cum = [0]
  for (const a of arcs) cum.push(cum[cum.length - 1] + a.len)
  const total = cum[cum.length - 1]
  const view: Point[] = []
  for (let k = 0; k <= n; k++) {
    const s = Math.min((k / n) * total, total)
    let i = 0
    while (i < arcs.length - 1 && s > cum[i + 1]) i++
    const a = arcs[i]
    const th = a.th1 + ((s - cum[i]) / a.len) * a.dth
    const lx = a.cx + a.r * Math.cos(th)
    const ly = a.cy + a.r * Math.sin(th)
    // Official transform chain rotate(180 512 768) translate(932 88.4257) rotate(90), flattened.
    view.push([92 + ly, 1447.5743 - lx])
  }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [x, y] of view) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  const sc = heightUnits / (maxY - minY)
  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2
  const c = VIEWBOX / 2
  return view.map(([x, y]) => [c + (x - cx) * sc, c + (y - cy) * sc])
}

/** Equal-arc-length Bernoulli lemniscate stations; both ends pinned to the crossing. */
function sampleLemniscate(n: number, a: number): Point[] {
  const m = 4096
  const raw: Point[] = []
  const cum = [0]
  for (let j = 0; j <= m; j++) {
    const th = Math.PI / 2 + (j / m) * 2 * Math.PI
    const s = Math.sin(th)
    const cth = Math.cos(th)
    const k = 1 + s * s
    raw.push([(a * cth) / k, (a * s * cth) / k])
  }
  for (let j = 1; j <= m; j++) {
    cum.push(cum[j - 1] + Math.hypot(raw[j][0] - raw[j - 1][0], raw[j][1] - raw[j - 1][1]))
  }
  const total = cum[m]
  const c = VIEWBOX / 2
  const out: Point[] = []
  let j = 0
  for (let k = 0; k <= n; k++) {
    const s = (k / n) * total
    while (j < m - 1 && cum[j + 1] < s) j++
    const seg = cum[j + 1] - cum[j] || 1
    const u = (s - cum[j]) / seg
    out.push([
      c + raw[j][0] + u * (raw[j + 1][0] - raw[j][0]),
      c + raw[j][1] + u * (raw[j + 1][1] - raw[j][1]),
    ])
  }
  out[0] = [c, c]
  out[n] = [c, c]
  return out
}

export function pointsToPath(pts: ReadonlyArray<Point>): string {
  let d = `M${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`
  for (let i = 1; i < pts.length; i++) {
    d += `L${pts[i][0].toFixed(2)} ${pts[i][1].toFixed(2)}`
  }
  return d
}

export function lerpPoints(
  from: ReadonlyArray<Point>,
  to: ReadonlyArray<Point>,
  t: number,
  out: Point[],
): Point[] {
  for (let i = 0; i < from.length; i++) {
    out[i][0] = from[i][0] + (to[i][0] - from[i][0]) * t
    out[i][1] = from[i][1] + (to[i][1] - from[i][1]) * t
  }
  return out
}

export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

export const SPIRAL_POINTS: ReadonlyArray<Point> = sampleSpiral(SEGMENTS, SPIRAL_HEIGHT)
export const LEMNISCATE_POINTS: ReadonlyArray<Point> = sampleLemniscate(SEGMENTS, LEMNISCATE_A)
export const SPIRAL_PATH = pointsToPath(SPIRAL_POINTS)
export const LEMNISCATE_PATH = pointsToPath(LEMNISCATE_POINTS)


/* ------------------------------------------------------------------ *
 * Phase driver
 * ------------------------------------------------------------------ */

export type LoaderInput = 'idle' | 'loading' | 'done'

export interface LoaderOptions {
  /**
   * Play the spiral -> lemniscate intro when loading begins (default true).
   * false starts directly on the lemniscate loop at phase 0 — for short,
   * unmount-on-completion loading surfaces where the morphs would dominate.
   */
  intro?: boolean
}

export type LoaderPhase =
  | 'idle'
  | 'introMorph'
  | 'introRelease'
  | 'loop'
  | 'loopEnding'
  | 'outroFill'
  | 'outroMorph'
  | 'settled'

export interface LoaderFrame {
  /** 0 = spiral, 1 = lemniscate. */
  e: number
  /** Snake dash window over the normalized path, or null for a solid stroke. */
  dash: { w0: number; len: number } | null
}

/**
 * Sequence: idle -> introMorph (solid spiral coils into the lemniscate)
 * -> introRelease (the closed stroke erodes into the snake over exactly one
 * period, landing on loop phase 0 with matching velocity) -> loop (dashoffset
 * only) -> loopEnding once 'done' arrives (keeps looping until the next
 * computed phase-0 boundary) -> outroFill (the head accelerates and re-bites
 * the tail over one period) -> outroMorph (solid lemniscate unwinds back into
 * the spiral) -> settled.
 */
export class LoaderDriver {
  private phaseValue: LoaderPhase
  private phaseStart = 0
  private loopStart = 0
  private outroStart = 0
  private doneRequested = false
  private pendingLoading = false
  private readonly intro: boolean
  onSettled: (() => void) | null = null

  constructor(initial: LoaderInput = 'idle', opts: LoaderOptions = {}) {
    this.intro = opts.intro !== false
    this.phaseValue =
      initial === 'loading'
        ? this.intro
          ? 'introMorph'
          : 'loop'
        : initial === 'done'
          ? 'settled'
          : 'idle'
  }

  get phase(): LoaderPhase {
    return this.phaseValue
  }

  get animating(): boolean {
    return this.phaseValue !== 'idle' && this.phaseValue !== 'settled'
  }

  input(kind: LoaderInput, now: number): void {
    const p = this.phaseValue
    if (kind === 'loading') {
      if (p === 'idle' || p === 'settled') {
        if (this.intro) {
          this.phaseValue = 'introMorph'
        } else {
          this.phaseValue = 'loop'
          this.loopStart = now
        }
        this.phaseStart = now
      } else if (p === 'loopEnding') {
        this.phaseValue = 'loop'
        this.doneRequested = false
      } else if (p === 'outroFill' || p === 'outroMorph') {
        this.pendingLoading = true
      } else {
        this.doneRequested = false
      }
    } else if (kind === 'done') {
      if (p === 'loop') {
        this.phaseValue = 'loopEnding'
        const n = Math.max(1, Math.ceil((now - this.loopStart) / PERIOD_MS))
        this.outroStart = this.loopStart + n * PERIOD_MS
      } else if (p === 'introMorph' || p === 'introRelease') {
        this.doneRequested = true
      } else if (p === 'idle') {
        this.phaseValue = 'settled'
      }
      this.pendingLoading = false
    } else {
      this.phaseValue = 'idle'
      this.doneRequested = false
      this.pendingLoading = false
    }
  }

  frame(now: number): LoaderFrame {
    for (let guard = 0; guard < 8; guard++) {
      const el = now - this.phaseStart
      switch (this.phaseValue) {
        case 'idle':
        case 'settled':
          return { e: 0, dash: null }
        case 'introMorph': {
          if (el >= MORPH_MS) {
            this.phaseValue = 'introRelease'
            this.phaseStart += MORPH_MS
            continue
          }
          return { e: easeInOutCubic(el / MORPH_MS), dash: null }
        }
        case 'introRelease': {
          if (el >= PERIOD_MS) {
            this.phaseStart += PERIOD_MS
            if (this.doneRequested) {
              this.phaseValue = 'outroFill'
              this.doneRequested = false
            } else {
              this.phaseValue = 'loop'
              this.loopStart = this.phaseStart
            }
            continue
          }
          const u = el / PERIOD_MS
          const head = SNAKE + u
          const len = SNAKE + (1 - SNAKE) * Math.pow(1 - u, 3)
          return { e: 1, dash: { w0: (head - len + 1) % 1, len } }
        }
        case 'loop':
        case 'loopEnding': {
          if (this.phaseValue === 'loopEnding' && now >= this.outroStart) {
            this.phaseValue = 'outroFill'
            this.phaseStart = this.outroStart
            continue
          }
          const phi = ((now - this.loopStart) / PERIOD_MS) % 1
          return { e: 1, dash: { w0: phi, len: SNAKE } }
        }
        case 'outroFill': {
          if (el >= PERIOD_MS) {
            this.phaseValue = 'outroMorph'
            this.phaseStart += PERIOD_MS
            continue
          }
          const u = el / PERIOD_MS
          return { e: 1, dash: { w0: u, len: SNAKE + (1 - SNAKE) * u * u * u } }
        }
        case 'outroMorph': {
          if (el >= MORPH_MS) {
            this.phaseValue = 'settled'
            this.phaseStart += MORPH_MS
            if (this.onSettled) this.onSettled()
            if (this.pendingLoading) {
              this.pendingLoading = false
              this.phaseValue = 'introMorph'
            }
            continue
          }
          return { e: 1 - easeInOutCubic(el / MORPH_MS), dash: null }
        }
      }
    }
    return { e: 0, dash: null }
  }
}
